import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Radar, MessageSquareText, Building2, Package, Users, GitCompareArrows,
  FileSearch, ListChecks, CalendarDays, ShieldCheck, ClipboardCheck, Truck, FileSignature,
  Bookmark, FileBarChart, Database, Settings, ScrollText, LogOut, Search, Globe, ChevronRight,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { api } from "../lib/api";

const NAV = [
  { to: "/app/dashboard", key: "nav.dashboard", icon: LayoutDashboard },
  { to: "/app/radar", key: "nav.radar", icon: Radar },
  { to: "/app/copilot", key: "nav.copilot", icon: MessageSquareText },
  { section: "Profilo" },
  { to: "/app/sito", key: "nav.sito", icon: Building2 },
  { to: "/app/prodotti", key: "nav.prodotti", icon: Package },
  { to: "/app/clienti", key: "nav.clienti", icon: Users },
  { section: "Operazioni" },
  { to: "/app/change-impact", key: "nav.change", icon: GitCompareArrows },
  { to: "/app/documents", key: "nav.documents", icon: FileSearch },
  { to: "/app/tasks", key: "nav.tasks", icon: ListChecks },
  { to: "/app/calendar", key: "nav.calendar", icon: CalendarDays },
  { section: "Intelligence" },
  { to: "/app/inspection", key: "nav.inspection", icon: ShieldCheck },
  { to: "/app/readiness", key: "nav.readiness", icon: ClipboardCheck },
  { to: "/app/supplier", key: "nav.supplier", icon: Truck, soon: true },
  { to: "/app/commitments", key: "nav.commitments", icon: FileSignature, soon: true },
  { to: "/app/watchlist", key: "nav.watchlist", icon: Bookmark },
  { to: "/app/reports", key: "nav.reports", icon: FileBarChart },
  { section: "Sistema" },
  { to: "/app/sources", key: "nav.sources", icon: Database },
  { to: "/app/audit", key: "nav.audit", icon: ScrollText },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const { t, lang, setLanguage } = useI18n();
  const navigate = useNavigate();
  const [q, setQ] = useState("");

  const onSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/app/radar?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <div className="min-h-screen flex bg-[var(--ri-bg)]">
      {/* Sidebar */}
      <aside className="w-64 flex-shrink-0 bg-[#0F172A] text-slate-300 flex flex-col fixed h-screen z-50" data-testid="app-sidebar">
        <div className="px-5 py-5 border-b border-slate-800">
          <div className="font-head text-white font-bold text-lg tracking-tight">RegIntel <span className="text-blue-400">CDMO</span></div>
          <div className="ri-overline mt-1" style={{ color: "#475569" }}>Regulatory Operating System</div>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5">
          {NAV.map((item, i) =>
            item.section ? (
              <div key={i} className="ri-overline px-2 pt-4 pb-1" style={{ color: "#475569" }}>{item.section}</div>
            ) : (
              <NavLink key={item.to} to={item.to} data-testid={`nav-${item.key.split(".")[1]}`}
                className={({ isActive }) =>
                  `group flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                    isActive ? "bg-[#1E293B] text-white font-medium" : "text-slate-400 hover:text-white hover:bg-slate-800/60"
                  }`}>
                {({ isActive }) => (
                  <>
                    {isActive && <span className="absolute left-0 w-1 h-6 rounded-r bg-blue-500 -ml-3" />}
                    <item.icon size={17} strokeWidth={2} />
                    <span className="flex-1">{t(item.key)}</span>
                    {item.soon && <span className="text-[0.6rem] font-mono px-1.5 py-0.5 rounded bg-slate-800 text-slate-500">soon</span>}
                  </>
                )}
              </NavLink>
            )
          )}
        </nav>
        <div className="px-4 py-3 border-t border-slate-800 text-xs text-slate-500">
          <div className="text-slate-300 font-medium truncate">{user?.name}</div>
          <div className="truncate">{user?.email}</div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 ml-64 flex flex-col min-h-screen">
        <header className="sticky top-0 z-40 h-14 bg-white/90 backdrop-blur border-b border-slate-200 flex items-center gap-4 px-6">
          <form onSubmit={onSearch} className="flex items-center gap-2 flex-1 max-w-xl">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input data-testid="global-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder='Es: "Annex 1 isolator interventions"'
                className="w-full pl-9 pr-3 py-1.5 rounded-lg border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:bg-white" />
            </div>
          </form>
          <div className="flex items-center gap-3">
            <div className="flex items-center rounded-lg border border-slate-200 overflow-hidden text-xs font-medium">
              {["it", "en"].map((l) => (
                <button key={l} data-testid={`lang-${l}`} onClick={() => setLanguage(l)}
                  className={`px-2.5 py-1 ${lang === l ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"}`}>
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
            <span className="hidden md:inline-flex items-center gap-1 text-xs text-slate-500">
              <Globe size={14} /> {user?.role}
            </span>
            <button data-testid="logout-btn" onClick={logout}
              className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-red-600 transition-colors">
              <LogOut size={16} /> {t("common.logout")}
            </button>
          </div>
        </header>
        <main className="flex-1 p-6 lg:p-8 max-w-[1500px] w-full">{children}</main>
      </div>
    </div>
  );
}

export function PageHeader({ overline, title, subtitle, actions }) {
  return (
    <div className="flex items-start justify-between mb-6">
      <div>
        {overline && <div className="ri-overline mb-1">{overline}</div>}
        <h1 className="font-head text-2xl lg:text-3xl font-bold tracking-tight text-slate-900">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500 mt-1 max-w-2xl">{subtitle}</p>}
      </div>
      {actions}
    </div>
  );
}

export function Disclaimer({ text }) {
  return (
    <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 flex gap-2">
      <span className="font-semibold">Nota:</span><span>{text}</span>
    </div>
  );
}
